import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
// eslint-disable-next-line import/no-extraneous-dependencies
import { uniqBy } from 'lodash';

import type { OrdersResponseSuccess } from '@order/graphql';
import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  useLegacyOrderHistoryQuery,
  useOrderHistoryQuery,
} from '../../GraphQL/Orders.generated';

export const usePaginatedOrders = () => {
  const [canLoadMore, setCanLoadMore] = useState(true);
  const [page, setPage] = useState(1);
  const [orders, setOrders] = useState<Orders>([]);

  const [response, fetchOrderHistory] = useOrderHistory(page);

  const isLoading = response.fetching;
  const successResponse = response.data?.orders as OrdersResponseSuccess;
  const ordersResponse = useMemo(
    () => successResponse?.orders ?? [],
    [successResponse?.orders],
  );

  useFocusEffect(
    useCallback(() => {
      fetchOrderHistory();
    }, [fetchOrderHistory]),
  );

  useEffect(() => {
    const empty = orders.length === 0 && ordersResponse.length === 0;

    setCanLoadMore(empty || ordersResponse.length === 10);
    setOrders(uniqBy([...orders, ...ordersResponse], 'id'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersResponse]);

  const handleLoadMore = useCallback(() => {
    setPage(Number(page) + 1);
    fetchOrderHistory();
  }, [page, fetchOrderHistory, setPage]);

  return { orders, canLoadMore, isLoading, handleLoadMore };
};

/**
 * Feature-flag-controlled order history query.
 */
const useOrderHistory = (page: number) => {
  const shouldUseCancelQuery = useFeatureFlag(
    'CELS-951-short-order-cancellation-quick-links-enabled',
  );

  const query = shouldUseCancelQuery
    ? useOrderHistoryQuery
    : useLegacyOrderHistoryQuery;

  const variables = { input: { take: 10, page } };

  return query({
    variables,
    pause: true,
    requestPolicy: 'cache-and-network',
  });
};

type Orders = OrdersResponseSuccess['orders'];
